import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Brand, CampaignType, Currency, Strategy, StrategyStateEnum, TacosStrategyGroup } from "@front/m19-api-client";
import { CAMPAIGN_TYPE, StrategyService } from "@front/m19-injection-token";
import { CampaignTypeWithTacos, DspFunnelDescriptions, Marketplaces, SegmentEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, TacosStrategiesService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IButtonDirective,
  ICheckboxComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ITableTdDirective,
  ITooltipDirective,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AlgoTargetButtonComponent } from "@m19-board/strategies/algo-target-button/algo-target-button.component";
import { StrategyGridAdLineComponent } from "@m19-board/strategies/strategy-grid/strategy-grid-ad-line.component";
import { StrategyGridBrandPipe } from "@m19-board/strategies/strategy-grid/strategy-grid-brand.pipe";
import { StrategyGridInfoComponent } from "@m19-board/strategies/strategy-grid/strategy-grid-info.component";
import { StrategyGridTacticComponent } from "@m19-board/strategies/strategy-grid/strategy-grid-tactic.component";
import { StrategyGridTargetingsComponent } from "@m19-board/strategies/strategy-grid/strategy-grid-targetings.component";
import { StrategyGridColumn } from "@m19-board/strategies/strategy-grid/strategy-grid.component";
import {
  CampaignTypeStrategyLine,
  StrategyGridService,
} from "@m19-board/strategies/strategy-grid/strategy-grid.service";
import { StrategyLinkComponent } from "@m19-board/shared/strategy-link/strategy-link.component";
import { filter, map, Observable, switchMap, take, tap } from "rxjs";
import { StrategyGridDspFunnelComponent } from "@m19-board/strategies/strategy-grid/strategy-grid-dsp-funnel.component";
import { CampaignTypeBadgeComponent } from "@m19-board/shared/campaign-type-badge/campaign-type-badge.component";
import { StrategyGridDspCreativeTypeComponent } from "./strategy-grid-dsp-creative-type.component";

@Component({
  selector: "tr[app-strategy-grid-line]",
  imports: [
    ICheckboxComponent,
    StrategyLinkComponent,
    ITableTdDirective,
    AlgoTargetButtonComponent,
    StrategyGridInfoComponent,
    StrategyGridTacticComponent,
    StrategyGridAdLineComponent,
    StrategyGridTargetingsComponent,
    IButtonDirective,
    TranslocoDirective,
    StrategyGridBrandPipe,
    IDropdownMenuTriggerDirective,
    IDropdownMenuComponent,
    IDropdownMenuSeparatorComponent,
    IDropdownMenuItemDirective,
    ITooltipDirective,
    StrategyGridDspFunnelComponent,
    CampaignTypeBadgeComponent,
    IIconComponent,
    StrategyGridDspCreativeTypeComponent,
  ],
  templateUrl: "./strategy-grid-line.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyGridLineComponent<T extends CampaignTypeWithTacos> {
  private readonly authService = inject(AuthService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyService = inject(StrategyService);
  private readonly strategyListService = inject(StrategyGridService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  protected readonly campaignType = inject<T>(CAMPAIGN_TYPE);

  protected readonly DspFunnelDescriptions = DspFunnelDescriptions;

  strategy = input.required<CampaignTypeStrategyLine[T]>();
  gridColumns = input.required<StrategyGridColumn[]>();
  isSelected = input<boolean>();
  segmentIndex = input.required<Map<number, SegmentEx>>();
  brands = input.required<Brand[]>();

  selectedStrategy = output<void>();

  isChangingStrategyStatus = signal(false);
  isDeletingStrategy = signal(false);
  isUpdatingStrategy = computed(() => this.isChangingStrategyStatus() || this.isDeletingStrategy());

  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  currency = toSignal<Currency>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      map((am) => {
        if (this.campaignType === CampaignType.DSP) {
          return am.dspAdvertiser!.currency;
        }
        return Marketplaces[am.marketplace].currency;
      }),
    ),
    { requireSync: true },
  );
  accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, { requireSync: true });
  organizationId = toSignal<number>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(map((am) => am.resourceOrganizationId!)),
    { requireSync: true },
  );
  isReadonly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });

  selectStrategy(): void {
    this.selectedStrategy.emit();
  }

  changeStrategyStatus(): void {
    this.isChangingStrategyStatus.set(true);
    const s = this.strategy();
    const newState = s.state == StrategyStateEnum.PAUSED ? StrategyStateEnum.ENABLED : StrategyStateEnum.PAUSED;

    let updateStrategyStateRequest: Observable<TacosStrategyGroup | Strategy>;
    if (s.isTacosStrategy) {
      updateStrategyStateRequest = this.tacosStrategiesService.updateStrategyGroupState(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        s.strategyGroupId!,
        newState,
      );
    } else {
      updateStrategyStateRequest = this.strategyService.updateStrategyState(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.organizationId(),
        s.strategyId!,
        newState,
      );
    }

    const isActivating = newState === StrategyStateEnum.ENABLED;

    updateStrategyStateRequest.pipe(take(1)).subscribe({
      next: () => {
        this.isChangingStrategyStatus.set(false);
        this.toasterService.success(
          isActivating
            ? this.translocoService.translate("strategy-grid.strategy_activated")
            : this.translocoService.translate("strategy-grid.strategy_paused"),
        );
      },
      error: (err) => {
        this.isChangingStrategyStatus.set(false);
        this.toasterService.error(err, this.translocoService.translate("strategy-grid.strategy_state_update_error"));
      },
    });
  }

  protected deleteStrategy(): void {
    const strategy = this.strategy();
    const modalRef = this.openStrategyConfirmDeleteModal(strategy.strategyName);

    const deleteStrategyRequest = this.strategyListService.deleteStrategy(strategy, this.organizationId());

    modalRef
      .afterClosed()
      .pipe(
        take(1),
        filter((confirmed) => confirmed),
        tap(() => this.isDeletingStrategy.set(true)),
        switchMap(() => deleteStrategyRequest),
      )
      .subscribe({
        next: () => {
          this.isDeletingStrategy.set(false);
          this.toasterService.success(
            this.translocoService.translate("sp-strategy-group-page.strategy_successfully_deleted", [
              strategy.strategyName,
            ]),
            this.translocoService.translate("strategy-grid.strategy_deleted"),
          );
        },
        error: (err) => {
          this.isDeletingStrategy.set(false);
          this.toasterService.error(
            this.translocoService.translate("strategy-grid.error_deleting_strategy_strategyname_err", [
              strategy.strategyName,
              err,
            ]),
            this.translocoService.translate("strategy-grid.strategy_deletion_error"),
          );
        },
      });
  }

  private openStrategyConfirmDeleteModal(strategyName: string): ModalRef<boolean> {
    return this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("activity-service.strategy_deletion"),
      data: {
        message: this.translocoService.translate("strategy-grid.are_you_sure_you_want_to_delete_strategy", [
          strategyName,
        ]),
        confirmLabel: this.translocoService.translate("common.delete"),
      },
    });
  }
}
